import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { api } from "../api";
import { useAds, InFeedAd } from "../ads";
import { useAuth } from "../auth";
import { mediaUrl, fmtNum, fmtDur } from "../config";
import { VideoCard, PastStreamCard, LiveCard, Empty, Spinner } from "../components/cards";
import { orderFeed } from "../subscriptions";

const ChipRow = ({ chips, active, onSelect }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  const arrowCls =
    "hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-slate-300 transition-colors hover:bg-white/15 hover:text-white sm:flex";
  return (
    <div className="mb-6 flex items-center gap-2" data-testid="category-chips">
      <button onClick={() => scroll(-1)} className={arrowCls} aria-label="Scroll categories left" data-testid="chips-scroll-left">
        <ChevronLeft size={18} />
      </button>
      <div ref={ref} className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {chips.map((c) => (
          <button
            key={c}
            onClick={() => onSelect(c)}
            data-testid={`chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3.5 py-1.5 font-heading text-sm font-semibold transition-colors duration-200 ${
              active === c ? "bg-white text-black" : "bg-white/[0.08] text-slate-200 hover:bg-white/[0.16]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <button onClick={() => scroll(1)} className={arrowCls} aria-label="Scroll categories right" data-testid="chips-scroll-right">
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const MusicChipCard = ({ track }) => (
  <Link to="/app/music" data-testid={`music-chip-card-${track.id}`} className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors hover:border-crimson/40">
    <div className="relative aspect-square overflow-hidden bg-surface">
      {mediaUrl(track.artwork) ? (
        <img src={mediaUrl(track.artwork)} alt={track.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Play size={26} className="text-slate-600" />
        </div>
      )}
      {track.durationSec > 0 && (
        <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white">{fmtDur(track.durationSec)}</span>
      )}
    </div>
    <div className="p-3">
      <p className="truncate font-heading text-sm font-semibold text-white">{track.title}</p>
      <p className="truncate text-xs text-slate-500">{track.artist} · {fmtNum(track.plays)} plays</p>
    </div>
  </Link>
);

const Home = () => {
  const { ads } = useAds();
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);
  const [live, setLive] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [chip, setChip] = useState("All");

  useEffect(() => {
    api.get("/live/streams").then((r) => setLive(Array.isArray(r) ? r : r.streams || [])).catch(() => {});
    api.get("/videos/categories").then((r) => setCategories(Object.keys(r || {}).filter((c) => c !== "All"))).catch(() => {});
    api.get("/music").then((r) => setTracks(Array.isArray(r) ? r : r.tracks || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (chip === "Music") return;
    setVideos(null);
    const cat = chip === "All" ? "" : `&category=${encodeURIComponent(chip)}`;
    api.get(`/videos?limit=48&offset=0&excludeFormat=short${cat}`).then((r) => setVideos(Array.isArray(r) ? r : r.videos || [])).catch(() => setVideos([]));
  }, [chip]);

  const liveNow = live.filter((s) => s.isLive || s.status === "live");
  const pastStreams = live.filter((s) => !(s.isLive || s.status === "live"));
  const every = ads?.rules?.feedAdEvery || 8;
  const chips = ["All", ...categories.filter((c) => c !== "Music"), "Music"];

  // Home shows ONLY normal videos (Shorts and Series are kept on their own pages).
  // Recommendation ordering: subscribed creators first (newest first), then a shuffled mix;
  // signed-out visitors get a fresh random mix each refresh.
  const feed = useMemo(() => {
    if (chip !== "All" || videos === null) return videos;
    // guarantee Home shows only normal videos even if the backend ignores excludeFormat
    const normalOnly = videos.filter((v) => (v.format || "video") !== "short");
    return orderFeed(normalOnly, user);
  }, [chip, videos, user]);

  const isMixedFeed = chip === "All" && feed && !Array.isArray(feed);
  const listItems = isMixedFeed ? [...(feed.fromSubs || []), ...(feed.rest || [])] : videos;

  return (
    <div className="mx-auto max-w-[1600px]" data-testid="home-page">
      <ChipRow chips={chips} active={chip} onSelect={setChip} />

      {chip === "Music" ? (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6" data-testid="home-music-grid">
          {tracks.map((t) => <MusicChipCard key={t.id} track={t} />)}
          {tracks.length === 0 && <Empty>No music yet.</Empty>}
        </div>
      ) : (
        <>
          {chip === "All" && liveNow.length > 0 && (
            <section className="mb-10">
              <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white sm:text-xl">Live now</h2>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {liveNow.map((s) => <LiveCard key={s.id} stream={s} />)}
              </div>
            </section>
          )}

          {videos === null ? (
            <Spinner />
          ) : (listItems || []).length === 0 ? (
            <Empty>{chip === "All" ? "No videos yet — check back soon." : `No videos in ${chip} yet.`}</Empty>
          ) : (
            <>
              {isMixedFeed && feed.personalized && feed.fromSubs.length > 0 && (
                <section className="mb-8" data-testid="home-from-subscriptions">
                  <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white sm:text-xl">From your subscriptions</h2>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                    {feed.fromSubs.map((v) => <VideoCard key={v.id} video={v} preview />)}
                  </div>
                </section>
              )}
              {isMixedFeed && <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white sm:text-xl" data-testid="home-for-you">{user ? "More for you" : "For you"}</h2>}
              <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="home-video-grid">
                {(isMixedFeed ? feed.rest : listItems).flatMap((item, i) => {
                  const rendered = [<VideoCard key={item.id} video={item} preview />];
                  if ((i + 1) % every === 0) rendered.push(<InFeedAd key={`ad-${i}`} />);
                  return rendered;
                })}
                {chip === "All" && pastStreams.map((s) => <PastStreamCard key={s.id} stream={s} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Home;
